import { Component, OnInit, OnDestroy, EventEmitter, Output, Input, ChangeDetectorRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NotificationsService } from '@lib/notifications';
import { SummaryApiProviderService } from '@project/data-providers';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, filter, map, takeUntil, tap } from 'rxjs/operators';

export type Tuss = {
  value: string;
  name: string;
};

@Component({
  selector: 'app-tuss-select',
  templateUrl: './tuss-select.component.html',
  styleUrls: ['./tuss-select.component.scss'],
})
export class TussSelectComponent implements OnInit, OnDestroy {
  @Output() onTussSelected = new EventEmitter<Tuss>();
  @Output() setIsSearching = new EventEmitter<boolean>();

  @Input() selectedTuss: any;
  @Input() disabled?: boolean = false;

  public isSearching = false;
  public serverSideCtrl: FormControl = new FormControl();
  public serverSideFilteringCtrl: FormControl = new FormControl();
  public filteredOptions: ReplaySubject<{ name: string; value: string }[]> = new ReplaySubject<any[]>(1);

  protected _onDestroy = new Subject<void>();

  constructor(
    private summaryApiProviderService: SummaryApiProviderService,
    private notificationsService: NotificationsService,
    private cdrRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.serverSideFilteringCtrl.valueChanges
      .pipe(
        map((value: string) => value.trim()),
        filter((search) => !!search && search.length > 2),
        tap(() => {
          this.isSearching = true;
          this.setIsSearching.emit(true);
        }),
        takeUntil(this._onDestroy),
        debounceTime(500),
        map((search) => this.summaryApiProviderService.getTussBySearch(search)),
      )
      .subscribe((filteredOptions) => {
        filteredOptions.subscribe(
          (tusses) => {
            this.isSearching = false;
            this.setIsSearching.emit(false);

            const tussesOptions = tusses.map((option) => ({
              name: `${option.code} - ${option.procedure}`,
              value: option.id,
            }));
            this.filteredOptions.next(tussesOptions);
          },
          () => {
            this.isSearching = false;
            this.setIsSearching.emit(false);
            this.notificationsService.error({ message: 'Falha ao buscar TUSS' });
          },
        );
      });

    this.attemptToAutoFillTuss();
    this.handleTussSelectionChanges();
  }

  attemptToAutoFillTuss(): void {
    if (!this.selectedTuss) return;

    this.serverSideCtrl.setValue(this.selectedTuss, { emitEvent: false });

    const tussNameSubstr = this.selectedTuss?.name?.substring(0, 8);
    if (tussNameSubstr) this.serverSideFilteringCtrl.setValue(tussNameSubstr);

    this.filteredOptions.subscribe((options) => {
      const matchedOption = options.find((o) => o.value === this.selectedTuss?.value || this.selectedTuss?.id);
      if (!matchedOption) return;

      this.serverSideCtrl.setValue(matchedOption);
      this.cdrRef.detectChanges();
    });
  }

  handleTussSelectionChanges(): void {
    this.serverSideCtrl.valueChanges.subscribe((newTussValue: Tuss) => {
      this.onTussSelected.emit(newTussValue);
    });
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
